import axios from "axios";
import router from "@/router";
import keycloak from "@/keycloak.js";
import redirectToLoginWhenSessionExpires, { isAuthExpired, isLoginRoute, } from "./Auth";

const apiUrl = process.env.VUE_APP_API_URL;

// making axios instance a singleton so it can intercept all requests
// and redirect to login if the user takes an action when their session expires
const axiosInstance = axios.create({
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
    },
    baseURL: apiUrl,
})

const errorPages = {
    404: "/404",
    500: "/500",
};

let isRedirectingToKeycloak = false;

// add inteceptor to prevent nav / api calls if session expires
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401) {
            if (!keycloak.authenticated && !isRedirectingToKeycloak) {
                isRedirectingToKeycloak = true;

                keycloak.login({
                    redirectUri: window.location.origin + "/admin",
                });

                return new Promise(() => {});
            }

            return Promise.reject(error);
        }

        return Promise.reject(error);
    },
);

// add interceptor to use keycloak auth
axiosInstance.interceptors.request.use(async (config) => {
    if (keycloak.authenticated) {
        await keycloak.updateToken(30);

        config.headers = config.headers ?? {};
        config.headers["Authorization"] = `Bearer ${keycloak.token}`;
    }

    return config;
});



export default class BaseApi {
    construct() {}
    
    static getApiUrl() {
        return apiUrl;
    }

    static getApiBasePath() {
        return "api/";
    }

    static getApiVersionBasePath(version) {
        return `api/${version}/`;
    }

    static getAxiosInstanceDownload(url){
        return axios({
            url: url,
            method: "GET",
            // important / required to allow downloading file contents from backend
            responseType: "blob", 
          })
    }

    static async request(executor) {
        return await executor();
    }


    static async startRequest() {
        return Promise.resolve();
    }

    static getAxiosInstance()
    {
        return axiosInstance;
    }    
    
    static async get(url, data)
    {
        return this.request(() => this.getAxiosInstance().get(url, data));
    }

    static async post(url, data)
    {
        return this.request(() => this.getAxiosInstance().post(url, data));
    }

    static async put(url, data)
    {
        return this.request(() => this.getAxiosInstance().put(url, data));
    }

    static async patch(url, data)
    {
        return this.request(() => this.getAxiosInstance().patch(url, data));
    }

    static async delete(url, data)
    {
        return this.request(() => this.getAxiosInstance().delete(url, data));
    }

    
    throwNotImplementedError(functionName) {
        throw new Error(`API '${this.constructor.name}' method '${functionName}' is not implemented yet`)
    }

    throwOnRequiredArgument(functionName, argumentName) {
        throw new RangeError(`API '${this.constructor.name}' method '${functionName}' requires a value for argument '${argumentName}'!`)
    }
}